define('presentation/settings/webChats/viewModels/addWebChatWidgetViewModel',[
    'businessServices/authentication/accountPlanInfo',
    'businessServices/authentication/sessionAccountInfo',
    'businessServices/router/router',
    'businessServices/state/modelStateObserver',
    'common/promises/promiseFactory',
    'common/storage/commonState',
    'common/url/urlFormatter',
    'constants/colorConstants',
    'constants/liveReceptionistScriptConstants',
    'constants/liveReceptionistScriptTypeConstants',
    'presentation/settings/webChats/facades/addWebChatWidgetFacade',
    'presentation/settings/webChats/validators/addWebChatWidgetViewModelValidator',
    'presentation/settings/webChats/viewModels/addWebChatWidgetDomainViewModel',
    'settings/navigationConfiguration'
], function () {
    return function () {
        const self = this;

        const PromiseFactoryConstructor = require('common/promises/promiseFactory');
        const _promiseFactory = new PromiseFactoryConstructor();

        const SorterConstructor = require('common/collections/collectionSorter');
        const _sorter = new SorterConstructor();

        const AddWebChatWidgetDomainViewModel = require('presentation/settings/webChats/viewModels/addWebChatWidgetDomainViewModel');
        const ModelStateObserverConstructor = require('businessServices/state/modelStateObserver');

        const _accountPlanInfo = require('businessServices/authentication/accountPlanInfo');
        const _i18n = require('i18next');
        const _colorConstants = require('constants/colorConstants');
        const _commonState = require('common/storage/commonState');
        const _liveReceptionistScriptConstants = require('constants/liveReceptionistScriptConstants');
        const _liveReceptionistScriptTypeConstants = require('constants/liveReceptionistScriptTypeConstants');
        const _navigationConfiguration = require('settings/navigationConfiguration');
        const _router = require('businessServices/router/router');
        const _sessionAccountInfo = require('businessServices/authentication/sessionAccountInfo');
        const _urlFormatter = require('common/url/urlFormatter');

        let _disposables = [];
        let _facade = null;
        let _validator = null;

        const MAX_ITEMS_CAN_ADD = 10;
        const NONE_LIVE_RECEPTIONIST_SCRIPT = {
            id: 0,
            name: _i18n.t('addWebChatWidget:liveReceptionistScriptNone'),
            type: _commonState.types.liveReceptionistScript
        };

        const launcherTextColorDefault = _colorConstants.colorPrimaryWhite;
        const launcherBackgroundColorDefault = _colorConstants.colorSecondaryWarm600;
        const welcomeMessageDefault = _i18n.t('addWebChatWidget:welcomeMessageDefault');

        self.modelStateObserver = null;
        self.webChatWidgetsUrl = _navigationConfiguration.routesById.webChatWidgets.url;
        self.shouldScrollToTop = ko.observable(false);
        self.isCompositionComplete = ko.observable(false);
        self.webChatWidgetId = null;
        self.isNewWebChatWidget = ko.observable(false);

        self.isImpersonating = ko.pureComputed(() => _sessionAccountInfo.isImpersonating());
        self.headerI18nKey = ko.pureComputed(() => self.isNewWebChatWidget() ? "addWebChatWidget:addHeader" : "addWebChatWidget:editHeader");
        self.saveButtonTitle = ko.pureComputed(() => self.isNewWebChatWidget() ? _i18n.t('addWebChatWidget:add') : _i18n.t('addWebChatWidget:save'));

        self.displayName = ko.observable('').extend({observeState: true});
        self.launcherTextColor = ko.observable(launcherTextColorDefault).extend({observeState: true});
        self.launcherBackgroundColor = ko.observable(launcherBackgroundColorDefault).extend({observeState: true});
        self.welcomeMessage = ko.observable(welcomeMessageDefault).extend({observeState: true});
        self.welcomeMessagePlaceholder = welcomeMessageDefault;

        self.selectedLiveReceptionistScript = ko.observable(null).extend({observeState: true});
        self.addLiveReceptionistScriptUrl = _navigationConfiguration.routesById.addLiveReceptionistScript.url;
        self.availableLiveReceptionistScripts = ko.observableArray([]);
        self.placeholderMessage = _i18n.t('addWebChatWidget:pleaseSelect');
        self.liveReceptionistScriptsIsEnabled = ko.pureComputed(() => {
            return !self.showNoWebChatPackageWarningMessage() && !self.showNoLiveReceptionistScriptsWarningMessage();
        });
        self.liveReceptionistScripts = ko.pureComputed(() => {
            const liveReceptionistScripts = self.availableLiveReceptionistScripts()
                .filter(script => isActiveWebChatScript(script) || isSelectedScript(script));
            _sorter.sort(liveReceptionistScripts, "name", true);
            return liveReceptionistScripts;
        });

        self.authorizedDomains = ko.observableArray([]).extend({observeState: true});
        self.domainPlaceholder = _i18n.t('addWebChatWidget:domainPlaceholder');
        self.canAddAnotherDomain = ko.pureComputed(() => self.authorizedDomains().length < MAX_ITEMS_CAN_ADD);
        self.canDeleteDomain = ko.pureComputed(() => self.authorizedDomains().length > 0);
        self.addDomainBtnText = ko.pureComputed(() => {
            if (self.canAddAnotherDomain()) {
                return _i18n.t('addWebChatWidget:addDomain');
            } else {
                return _i18n.t('addWebChatWidget:disabledAddDomain');
            }
        });

        self.codeSnippetMoreInformationUrl = _urlFormatter.buildFrontendUrl(["blog", "help-web-chat-settings"]);
        self.codeSnippetPlaceholder = _i18n.t('addWebChatWidget:codeSnippetPlaceholder');
        self.showCodeSnippet = ko.pureComputed(() => {
            return self.isNewWebChatWidget() === false && self.selectedLiveReceptionistScript() !== null && self.selectedLiveReceptionistScript().id !== NONE_LIVE_RECEPTIONIST_SCRIPT.id;
        });
        self.codeSnippet = ko.pureComputed(() => {
            if (self.showCodeSnippet() === false) {
                return '';
            }
            const widgetUrl = _urlFormatter.buildWebChatWidgetUrl(self.webChatWidgetId);
            return `<!-- PATLive Web Chat -->
<script type="text/javascript" src="${widgetUrl}"></script>
<!-- End PATLive Web Chat -->`;
        });

        self.createLiveReceptionistScriptLinkText = _i18n.t('addWebChatWidget:createLiveReceptionistScript');
        self.noLiveReceptionistScriptsWarningMessage = _i18n.t('addWebChatWidget:noLiveReceptionistScriptWarningMessage');
        self.noWebChatPackageWarningMessage = _i18n.t('addWebChatWidget:noWebChatPackageWarningMessage');
        self.showNoLiveReceptionistScriptsWarningMessage = ko.pureComputed(() => self.liveReceptionistScripts().length === 0);
        self.showNoWebChatPackageWarningMessage = ko.pureComputed(() => {
            return self.isImpersonating() && (_accountPlanInfo.hasWebChatPackage() === false || _accountPlanInfo.isWebChatOnboardingComplete() === false);
        });
        self.showValidationWarnings = ko.observable(false);
        
        self.onAddDomainClick = () => {
            if (self.canAddAnotherDomain() === false) {
                return;
            }
            _addDefaultDomain();
        };

        self.onRemoveDomainClick = (domainToDelete) => {
            if (self.canDeleteDomain() === false) {
                return;
            }
            self.authorizedDomains.remove(domainToDelete);
        };

        self.save = () => {
            return _promiseFactory.deferWithMinimumWait((promise) => {
                if (self.selectedLiveReceptionistScript() === NONE_LIVE_RECEPTIONIST_SCRIPT) {
                    self.selectedLiveReceptionistScript(null);
                }
                _removeDuplicateFieldsBeforeValidation();
                self.validate()
                    .fail(promise.reject)
                    .done((isValid) => {
                        if (isValid === false) {
                            promise.resolve();
                            return;
                        }

                        _removeEmptyFieldsBeforeSave();

                        const webChatWidget = {
                            webChatWidgetId: self.webChatWidgetId || null,
                            displayName: self.displayName(),
                            launcherTextColor: self.launcherTextColor(),
                            launcherBackgroundColor: self.launcherBackgroundColor(),
                            welcomeMessage: self.welcomeMessage(),
                            liveReceptionistScriptId: self.selectedLiveReceptionistScript() ? self.selectedLiveReceptionistScript().id : null,
                            authorizedDomains: self.authorizedDomains().map((d) => {
                                return {
                                    domain: d.domain()
                                };
                            })
                        };

                        const facadePromise = self.isNewWebChatWidget() ?
                            _facade.createWebChatWidget(webChatWidget):
                            _facade.updateWebChatWidget(webChatWidget);

                        facadePromise
                            .fail(promise.reject)
                            .done(() => {
                                self.modelStateObserver.saveData();
                                _router.navigate(self.webChatWidgetsUrl);
                                promise.resolve();
                            });
                    });
            });
        };

        self.cancelForm = () => {
            return _promiseFactory.defer((promise) => {
                self.modelStateObserver.navigateWithoutWarning = true;
                _router.navigate(self.webChatWidgetsUrl);
                promise.resolve();
            });
        };

        self.validate = () => {
            self.shouldScrollToTop(false);
            return _promiseFactory.defer((promise) => {
                _validator.validate()
                    .fail(promise.reject)
                    .done((isFormValid) => {
                        if (isFormValid === false) {
                            self.showValidationWarnings(true);
                        }
                        promise.resolve(isFormValid);
                    });
            });
        };

        const isActiveWebChatScript = (script) => {
            return script.status === _liveReceptionistScriptConstants.status.active.id &&
                script.scriptType === _liveReceptionistScriptTypeConstants.WebChat;
        };

        const isSelectedScript = (script) => {
             return self.selectedLiveReceptionistScript() !== null &&
                 self.selectedLiveReceptionistScript() !== undefined &&
                 self.selectedLiveReceptionistScript().id === script.id;
        };

        const _removeDuplicateFieldsBeforeValidation = () => {
            const duplicateDomains = self.authorizedDomains().filter((domain, index) => {
                return self.authorizedDomains().find((d, i) => {
                    return d.domain().trim() === domain.domain().trim() &&
                        index !== 0 &&
                        index !== i;
                });
            });
            self.authorizedDomains.removeAll(duplicateDomains);
        };

        const _removeEmptyFieldsBeforeSave = () => {
            const emptyDomains = self.authorizedDomains().filter((domain) => domain.domain().trim() === "");
            self.authorizedDomains.removeAll(emptyDomains);
        };

        const _setLiveReceptionistScript = (liveReceptionistScriptId) => {
            if (liveReceptionistScriptId === null) {
                self.selectedLiveReceptionistScript(NONE_LIVE_RECEPTIONIST_SCRIPT);
                return;
            }

            const liveReceptionistScript = self.liveReceptionistScripts().find(script => script.id === liveReceptionistScriptId);
            self.selectedLiveReceptionistScript(liveReceptionistScript || NONE_LIVE_RECEPTIONIST_SCRIPT);
        };

        const _addDefaultDomain = () => {
            const domain = new AddWebChatWidgetDomainViewModel(self);
            self.modelStateObserver.addChildObserver(domain.modelStateObserver);
            self.authorizedDomains.push(domain);
        };

        const _setDomains = (authorizedDomains) => {
            if (authorizedDomains.length === 0) {
                _addDefaultDomain();
                return;
            }

            authorizedDomains.map((webChatDomain) => {
                const domain = new AddWebChatWidgetDomainViewModel(self);
                domain.webChatWidgetAuthorizedDomainId = webChatDomain.webChatWidgetAuthorizedDomainId;
                domain.domain(ko.unwrap(webChatDomain.domain));
                self.modelStateObserver.addChildObserver(domain.modelStateObserver);
                self.authorizedDomains.push(domain);
            });
        };
        
        const _populateExistingWebChatForm = (webChatWidgetId) => {
            return _promiseFactory.deferIndefinitely((promise) => {
                _facade.getWebChatWidget(webChatWidgetId)
                    .fail(promise.reject)
                    .done((webChat) => {
                        const {
                            displayName,
                            launcherTextColor,
                            launcherBackgroundColor,
                            liveReceptionistScriptId,
                            welcomeMessage,
                            authorizedDomains,
                        } = webChat;

                        self.displayName(displayName);
                        self.launcherTextColor(launcherTextColor);
                        self.launcherBackgroundColor(launcherBackgroundColor);
                        self.welcomeMessage(welcomeMessage);

                        _setLiveReceptionistScript(liveReceptionistScriptId);
                        _setDomains(authorizedDomains);

                        promise.resolve();
                    });
            });
        };

        const _populateNewWebChatForm = () => {
            return _promiseFactory.deferIndefinitely((promise) => {
                _setLiveReceptionistScript(null);
                _addDefaultDomain();
                promise.resolve();
            });
        };

        self.compositionComplete = () => {
            self.isCompositionComplete(true);
            if (self.webChatWidgetId === null) {
                self.isNewWebChatWidget(true);
            }
        };

        self.detached = () => {
            self.isCompositionComplete(false);
            _disposables.forEach(d => d.dispose());
            _disposables = [];
        };

        self.activate = (webChatWidgetId) => {
            if (webChatWidgetId) {
                self.webChatWidgetId = webChatWidgetId;
            }

            const FacadeConstructor = require('presentation/settings/webChats/facades/addWebChatWidgetFacade');
            _facade = new FacadeConstructor();
            _facade.init(_promiseFactory);

            const ValidatorConstructor = require('presentation/settings/webChats/validators/addWebChatWidgetViewModelValidator');
            _validator = new ValidatorConstructor();

            return _initialize();
        };

        const _initialize = () => {
            self.modelStateObserver = new ModelStateObserverConstructor(self, true);

            let initPromiseFactory = new PromiseFactoryConstructor();

            initPromiseFactory.defer((initPromise) => {
                _facade.getLiveReceptionistScripts()
                    .fail(initPromise.reject)
                    .done((liveReceptionistScripts) => {
                        self.availableLiveReceptionistScripts(liveReceptionistScripts);
                        let populatePromise;
                        if (self.webChatWidgetId) {
                            populatePromise = _populateExistingWebChatForm(self.webChatWidgetId);
                        } else {
                            populatePromise = _populateNewWebChatForm();
                        }
                        populatePromise.done(() => {
                            initPromise.resolve();
                        });
                    });
            });

            _promiseFactory.defer((initializePromise) => {
                initPromiseFactory.wait()
                    .done(() => {
                        self.modelStateObserver.commitData();
                        _validator.registerViewModel(self);
                        initializePromise.resolve();
                    });
            });

            return _promiseFactory.wait();
        };
    };
});
